import { OrderGuideUploadMode } from '@choco-admin/types';
import {
  Button,
  Card,
  CardBody,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
} from '@heroui/react';
import React from 'react';
import { FiUpload, FiX } from 'react-icons/fi';

import { ChocoSelect } from '../../../components/FormComponents';
import { UploadGroup } from '../types';

const MODE_OPTIONS: {
  value: OrderGuideUploadMode;
  label: string;
  description: string;
}[] = [
  {
    value: 'Update',
    label: 'Update',
    description:
      'Adds new products and updates existing ones. Removes nothing.',
  },
  {
    value: 'AddOnly',
    label: 'Add only',
    description: 'Only adds products that are not yet in the order guide.',
  },
  {
    value: 'Replace',
    label: 'Replace (destructive)',
    description:
      'The order guide will contain exactly the products in this file — everything else is removed.',
  },
];

const chatLabel = (group: UploadGroup): string => {
  const { chatInfo } = group;
  if (!chatInfo) return group.chatId;
  const name = chatInfo.chatName ?? chatInfo.restaurantName;
  const parts = [name, chatInfo.supplierName].filter(Boolean);
  return parts.length > 0 ? parts.join(' — ') : group.chatId;
};

const guideLabel = (group: UploadGroup): string => {
  if (group.targetGuide) {
    // The id is rendered underneath, so an unnamed guide gets a readable label
    return group.targetGuide.name ?? 'Unnamed order guide';
  }
  if (group.orderGuideId) return group.orderGuideId;
  return 'New order guide (created on upload)';
};

interface UploadPreviewProps {
  groups: UploadGroup[];
  mode: OrderGuideUploadMode;
  onModeChange: (mode: OrderGuideUploadMode) => void;
  onUpload: () => void;
  onReset: () => void;
  canUpload: boolean;
}

export const UploadPreview: React.FC<UploadPreviewProps> = ({
  groups,
  mode,
  onModeChange,
  onUpload,
  onReset,
  canUpload,
}) => {
  const totalProducts = groups.reduce(
    (sum, group) => sum + group.records.length,
    0
  );
  const selectedMode = MODE_OPTIONS.find((option) => option.value === mode);

  return (
    <Card>
      <CardBody className="space-y-4">
        <div>
          <h2 className="text-lg font-semibold text-foreground">
            Upload preview
          </h2>
          <p className="text-sm text-default-500">
            {totalProducts} product{totalProducts === 1 ? '' : 's'} across{' '}
            {groups.length} order guide{groups.length === 1 ? '' : 's'}.
          </p>
        </div>

        <Table aria-label="Order guides in this upload" removeWrapper isStriped>
          <TableHeader>
            <TableColumn>CHAT</TableColumn>
            <TableColumn>ORDER GUIDE</TableColumn>
            <TableColumn className="text-right">PRODUCTS</TableColumn>
            <TableColumn className="text-right">CATEGORIES</TableColumn>
            <TableColumn className="text-right">EXISTING PRODUCTS</TableColumn>
          </TableHeader>
          <TableBody>
            {groups.map((group) => (
              <TableRow key={group.key}>
                <TableCell>
                  <div>{chatLabel(group)}</div>
                  <div className="text-xs text-default-400 font-mono">
                    {group.chatId}
                  </div>
                </TableCell>
                <TableCell>
                  <div>{guideLabel(group)}</div>
                  {group.targetGuide && (
                    <div className="text-xs text-default-400 font-mono">
                      {group.targetGuide.id}
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  {group.records.length}
                </TableCell>
                <TableCell className="text-right">
                  {group.categoryCount}
                </TableCell>
                <TableCell className="text-right">
                  {group.targetGuide?.itemsCount ?? '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <div className="max-w-md space-y-1">
          <ChocoSelect
            label="Update mode"
            options={MODE_OPTIONS.map(({ value, label }) => ({
              value,
              label,
            }))}
            value={mode}
            onSelectionChange={(value) => {
              if (
                value === 'Update' ||
                value === 'AddOnly' ||
                value === 'Replace'
              ) {
                onModeChange(value);
              }
            }}
          />
          {selectedMode && (
            <p
              className={`text-sm ${
                mode === 'Replace' ? 'text-danger' : 'text-default-500'
              }`}
            >
              {selectedMode.description}
            </p>
          )}
        </div>

        <div className="flex gap-2">
          <Button
            color={mode === 'Replace' ? 'danger' : 'primary'}
            startContent={<FiUpload />}
            onPress={onUpload}
            isDisabled={!canUpload}
          >
            {mode === 'Replace' ? 'Replace order guides' : 'Upload'}
          </Button>
          <Button variant="flat" startContent={<FiX />} onPress={onReset}>
            Start over
          </Button>
        </div>
      </CardBody>
    </Card>
  );
};
